import { delay } from '@/utils/delay';
import { createMessenger, Unsubscribable, v1 } from '@userlike/messenger';
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from 'react';

interface ChatContextData {
  initializeChat: (languageCode: 'en' | 'de' | 'fr' | 'it') => void;
  openChat: () => void;
  setHideChat: (value: boolean) => void;
}

export const ChatContext = createContext<ChatContextData>(
  {} as ChatContextData,
);

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const [chat, setChat] = useState<any>(null);
  const [chatState, setChatState] = useState<v1.Api>({} as v1.Api);
  const [eventsSubscriber, setEventsSubscriber] = useState<Unsubscribable>(
    {} as Unsubscribable,
  );
  const [hideChat, setHideChat] = useState(false);

  const initializeChat = (languageCode: 'en' | 'de' | 'fr' | 'it' = 'en') => {
    const widgetKeys = {
      en: 'd407a9d2dd6e4c6ab6673cb8ea9dc682c3aaae884e8f4c9993ff47af8ebb6256',
      de: '29f82a94e2754fcea7eae776f1721fb8467689f2495f4e1f98ce789aaaad4969',
      fr: '1c2f398f968041a4847409170ac1660fdc458ad853d74e238d9d69cff82f1c1c',
      it: '6da32fa4c5394ac3964ee53fe9718741a2d88dfe49f1473c91dc2982dcd7b542',
    };
    const widgetKey = widgetKeys[languageCode] || widgetKeys.en;
    async function createApi(): Promise<v1.Api> {
      const result = (await createMessenger({
        version: 1,
        widgetKey,
      })) as { value: any };

      const { api } = result.value;
      return api;
    }

    const events = [
      'pageview',
      'mousemove',
      'scroll',
      'keydown',
      'click',
      'touchstart',
    ];

    if (chat) {
      chat.unmount();
      setChat(null);
    }

    const handleFirstInteraction = () => {
      const userlike = createApi();

      userlike.then((messenger) => {
        setChat((prevState: v1.Api) => (!prevState ? messenger : prevState));

        const sub = messenger.state$.subscribe({
          next: async ({ state }) => {
            if (state === 'maximized') {
              document.documentElement.classList.add(
                'max-[500px]:!scroll-auto',
              );
            }
            if (state === 'minimized') {
              await delay(50);
              document.documentElement.classList.remove(
                'max-[500px]:!scroll-auto',
              );
            }
          },
        });

        setEventsSubscriber(sub);
      });

      events.forEach((event) => {
        window.removeEventListener(event, handleFirstInteraction);
      });
    };

    events.forEach((event) => {
      window.addEventListener(event, handleFirstInteraction);
    });
  };

  useEffect(() => {
    if (hideChat && chat) {
      chat.unmount();
      return;
    }
    if (chat) {
      setChatState(chat.getState().state);

      chat.mount();
    }
  }, [chat, hideChat]);

  const openChat = () => {
    if (chat && chat.getState().state === 'minimized') {
      chat.maximize();
    }
  };

  return (
    <ChatContext.Provider
      value={{
        initializeChat,
        openChat,
        setHideChat,
      }}>
      {children}
    </ChatContext.Provider>
  );
};

export function useChat(): ChatContextData {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error('UseChat must be used within an UseChatProvider');
  }

  return context;
}
